import { forwardRef, useId } from 'react'
import type { InputHTMLAttributes, ReactNode } from 'react'
import { cn } from '../../lib/cn'

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string
  error?: string | null
  hint?: ReactNode
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, error, hint, className, id, ...props },
  ref,
) {
  const reactId = useId()
  const inputId = id ?? reactId
  return (
    <label htmlFor={inputId} className="flex flex-col gap-2">
      {label && (
        <span className="mono-tag text-black/70">{label}</span>
      )}
      <input
        ref={ref}
        id={inputId}
        className={cn(
          'w-full border border-black/[0.12] rounded-lg px-4 py-2.5 text-body font-330 bg-white text-black placeholder:text-black/35',
          'transition-colors',
          'hover:border-black/[0.22]',
          'focus:outline-none focus:border-black/30',
          'disabled:opacity-40 disabled:cursor-not-allowed',
          error && 'border-brand-pink',
          className,
        )}
        aria-invalid={!!error || undefined}
        aria-describedby={error ? `${inputId}-err` : hint ? `${inputId}-hint` : undefined}
        {...props}
      />
      {error ? (
        <span id={`${inputId}-err`} className="text-body-sm font-450 text-black">
          {error}
        </span>
      ) : hint ? (
        <span id={`${inputId}-hint`} className="text-body-sm font-330 text-black/55">
          {hint}
        </span>
      ) : null}
    </label>
  )
})
